<template>
    <Flippable class="trends-detail-card">
        <template #front>
            <TrendsCard class="trends-detail-card__face">
                <template #image>
                    <PrismicImage :field="card.image" class="trends-detail-card__image" :imgix-params="{h: 200}" :alt="card.title" />
                </template>

                <template #content>
                    <h3 class="trends-detail-card__title">{{ card.title }}</h3>
                    <div class="trends-detail-card__subtitle">{{ card.subtitle }}</div>
                    <PrismicRichText v-if="card.shortDescription" class="trends-detail-card__short-description" :field="card.shortDescription" />
                    <VJoyTag v-if="card.releaseTag" variant="pending" size="medium" class="trends-detail-card__date">
                        {{ card.releaseTag }}
                    </VJoyTag>
                </template>

                <template #footer>
                    <div class="trends-detail-card__read-more">
                        <span>{{ readMoreLabel }}</span>
                        <VJoyIcon name="arrow-right" size="xxsmall" />
                    </div>
                </template>
            </TrendsCard>
        </template>
        <template #back>
            <TrendsCard class="trends-detail-card__face trends-detail-card__back">
                <template #image>
                    <PrismicImage :field="card.image" class="trends-detail-card__image" :imgix-params="{h: 200}" :alt="card.title" />
                </template>

                <template #content>
                    <h3 class="trends-detail-card__title">{{ card.title }}</h3>
                    <div class="trends-detail-card__subtitle">{{ card.subtitle }}</div>
                    <PrismicRichText v-if="card.description" class="trends-detail-card__description" :field="card.description" />
                </template>

                <template #footer>
                    <VJoyTag v-if="card.releaseTag" variant="pending" size="medium" class="trends-detail-card__footer-date">
                        {{ card.releaseTag }}
                    </VJoyTag>
                    <VJoyButton v-if="showCtaButton && ctaWeb" variant="main" size="small" :href="ctaWeb.url" target="_blank" @click.stop>
                        {{ ctaWeb.text }}
                    </VJoyButton>
                </template>
            </TrendsCard>
        </template>
    </Flippable>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/client';
    import {PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {TrendsDetailsCarouselSliceDefaultPrimaryCardsItem} from '@prismicio-types';
    import {VJoyIcon, VJoyTag, VJoyButton} from '@maltjoy/core-vue';
    import Flippable from '~/components/trends/Flippable.vue';
    import TrendsCard from '~/components/trends/TrendsCard.vue';

    const {card} = defineProps<{card: TrendsDetailsCarouselSliceDefaultPrimaryCardsItem}>();

    const ctaWeb = computed(() => {
        const cta = card.cta;
        if (!isFilled.link(cta) || cta.link_type !== 'Web') return null;
        return {url: cta.url, text: cta.text};
    });
    const showCtaButton = computed(() => !card.releaseTag && !!ctaWeb.value?.url && !!ctaWeb.value?.text);
    const readMoreLabel = computed(() => card.readMoreLabel ?? 'Read more');
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .trends-detail-card {
        width: 280px;

        &__face {
            display: flex;
            flex-direction: column;
            text-align: left;
            cursor: pointer;
            transition: box-shadow var(--joy-transition-duration-short) var(--joy-transition-timing-function);
        }

        h3 {
            @include text.getFontBySize(secondary-400);
            font-size: 40px;
        }

        &__date.joy-tag {
            position: absolute;
            top: var(--joy-core-spacing-3);
            right: var(--joy-core-spacing-3);
            background-color: var(--joy-color-brand-primary-70);
            color: var(--joy-color-neutral-0);
            font-family: var(--joy-font-family-title);
            font-size: var(--joy-font-size-primary-400);
        }

        &__subtitle {
            @include text.getFontBySize(primary-800);
            font-family: var(--joy-font-family-title);
            line-height: var(--joy-line-height-large);
            font-weight: var(--joy-font-weight-normal);
        }

        &__short-description {
            margin-top: var(--joy-core-spacing-2);
        }

        &__read-more {
            @include text.getFontBySize(primary-400);
            font-weight: var(--joy-font-weight-normal);
            text-decoration: underline;
            text-underline-offset: 1px;
            user-select: none;

            .joy-icon {
                text-decoration: none;
                position: relative;
                top: 1px;
                left: var(--joy-core-spacing-2);
                transition: transform var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            }
        }

        /* Hover state */
        &__face:hover {
            box-shadow: var(--joy-core-elevation-2);

            .joy-icon {
                transform: translate3d(var(--joy-core-spacing-1), 0, 0);
            }
        }

        /* Back face */
        &__back {
            position: absolute;
            inset: 0;

            .trends-card__image {
                position: absolute;
                opacity: 0.1;
            }

            .trends-detail-card__description {
                overflow: hidden;
                display: -webkit-box;
                line-clamp: 14;
                -webkit-line-clamp: 14; /* number of lines */
                -webkit-box-orient: vertical;
                margin-top: var(--joy-core-spacing-2);
            }
        }

        &__footer {
            &-date.joy-tag {
                background-color: var(--joy-color-brand-primary-70);
            }
        }
    }
</style>
